import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../components';
import { useApi } from '../api';
import { useUserTags } from '../user-tags';
import { READINESS_LEVELS } from '../question-set-types';
import { QUESTION_TYPES, QUESTION_TYPE_LABELS, type QuestionType } from '@padcanvas/shared';
import './BulkEditModal.css';

const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard'] as const;
const STATUS_OPTIONS = ['draft', 'live'] as const;

export interface BulkEditUpdates {
  difficulty?: 'easy' | 'medium' | 'hard';
  importStatus?: 'draft' | 'live';
  questionReadiness?: number;
  questionType?: QuestionType;
}

export interface TeacherOption {
  id: string;
  name: string;
}

export interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: Set<string>;
  onSuccess: () => void;
  /** Called to apply metadata updates. Caller provides the actual API call. */
  onBulkEdit: (ids: string[], updates: BulkEditUpdates) => Promise<{ updated: number }>;
  /** Optional: enables "Tag for teacher" section. Provide list of teachers and the bulk-tag callback. */
  teachers?: TeacherOption[];
  onBulkTagForUser?: (userId: string | undefined, tag: string, questionIds: string[]) => Promise<{ tag: string; tagged: number; skipped: number }>;
}

export function BulkEditModal({ isOpen, onClose, selectedIds, onSuccess, onBulkEdit, teachers, onBulkTagForUser }: BulkEditModalProps) {
  const queryClient = useQueryClient();
  const api = useApi();

  // Field states
  const [difficulty, setDifficulty] = useState<string>('');
  const [importStatus, setImportStatus] = useState<string>('');
  const [questionReadiness, setQuestionReadiness] = useState<number | null>(null);
  const [questionType, setQuestionType] = useState<string>('');

  // Tags
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState('');

  // Tag for teacher (admin-only feature)
  const [teacherTagUserId, setTeacherTagUserId] = useState('');
  const [teacherTagName, setTeacherTagName] = useState('');

  const { visibleTags, defaultTags } = useUserTags();

  const availableTags = [...new Set([...defaultTags, ...visibleTags.map(t => t.tag)])];

  const [isApplying, setIsApplying] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setDifficulty('');
    setImportStatus('');
    setQuestionReadiness(null);
    setQuestionType('');
    setSelectedTags([]);
    setNewTagInput('');
    setTeacherTagUserId('');
    setTeacherTagName('');
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const toggleArrayValue = (arr: string[], val: string, setter: (v: string[]) => void) => {
    setter(arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]);
  };

  const handleAddNewTag = useCallback(() => {
    const trimmed = newTagInput.trim();
    if (!trimmed || selectedTags.includes(trimmed)) return;
    // Create tag if it doesn't exist
    api.fetchApi<{ tag: string }>('/user/tags', { method: 'POST', body: JSON.stringify({ tag: trimmed }) })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['user-tags'] });
      })
      .catch(() => {});
    setSelectedTags(prev => [...prev, trimmed]);
    setNewTagInput('');
  }, [newTagInput, selectedTags, api, queryClient]);

  const handleApply = useCallback(async () => {
    const ids = [...selectedIds];
    setIsApplying(true);
    setResult(null);

    try {
      const messages: string[] = [];

      // 1. Apply metadata updates
      const updates: BulkEditUpdates = {};
      if (difficulty) {
        updates.difficulty = difficulty as BulkEditUpdates['difficulty'];
      }
      if (importStatus) {
        updates.importStatus = importStatus as BulkEditUpdates['importStatus'];
      }
      if (questionReadiness !== null) {
        updates.questionReadiness = questionReadiness;
      }
      if (questionType) {
        updates.questionType = questionType as QuestionType;
      }

      if (Object.keys(updates).length > 0) {
        const res = await onBulkEdit(ids, updates);
        messages.push(`Updated ${res.updated} question(s)`);
      }

      // 2. Apply tags (add each selected question to each selected tag)
      if (selectedTags.length > 0) {
        for (const tag of selectedTags) {
          for (const qId of ids) {
            try {
              await api.fetchApi(`/user/tags/${encodeURIComponent(tag)}/items/question/${qId}`, { method: 'PUT' });
            } catch {
              // skip individual failures
            }
          }
        }
        messages.push(`Applied ${selectedTags.length} tag(s) to ${ids.length} question(s)`);
        queryClient.invalidateQueries({ queryKey: ['user-tags'] });
      }

      // 3. Tag questions (admin feature). Teacher is OPTIONAL — leave blank to tag as
      //    the current admin; recognized system tags (e.g. StudentPlacement) route to the
      //    shared pool server-side regardless.
      if (teacherTagName.trim() && onBulkTagForUser) {
        const res = await onBulkTagForUser(teacherTagUserId || undefined, teacherTagName.trim(), ids);
        const who = teacherTagUserId ? ` for ${teachers?.find(t => t.id === teacherTagUserId)?.name || 'teacher'}` : '';
        messages.push(`Tagged ${res.tagged} question(s) as "${res.tag}"${who}${res.skipped > 0 ? ` (${res.skipped} skipped)` : ''}`);
      }

      queryClient.invalidateQueries({ queryKey: ['question-list'] });
      queryClient.invalidateQueries({ queryKey: ['question-detail'] });

      setResult(messages.join('. ') || 'No changes applied');
      onSuccess();
    } catch (err) {
      setResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsApplying(false);
    }
  }, [selectedIds, difficulty, importStatus, questionReadiness, questionType, selectedTags, teacherTagUserId, teacherTagName, teachers, api, queryClient, onSuccess, onBulkEdit, onBulkTagForUser]);

  const hasChanges = !!difficulty || !!importStatus || questionReadiness !== null || !!questionType || selectedTags.length > 0 || !!teacherTagName.trim();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Bulk Edit — ${selectedIds.size} question(s)`} size="md">
      <div className="bulk-edit-content">

        {/* Target Exams and Class were here. Neither is stored any more — the
            question document carries only `boardGrades` — so both chips edited a
            field the write funnel then stripped. Bulk pool editing needs a
            board-grade key control, which is a different shape from these two
            independent chip rows and is deliberately not faked with them. */}

        {/* Difficulty */}
        <div className="bulk-edit-field">
          <div className="bulk-edit-field-header">
            <label>Difficulty</label>
            <span className="bulk-edit-mode-label">override</span>
          </div>
          <div className="bulk-edit-chips">
            {DIFFICULTY_OPTIONS.map(d => (
              <button
                key={d}
                type="button"
                className={`bulk-edit-chip ${difficulty === d ? 'active' : ''}`}
                onClick={() => setDifficulty(difficulty === d ? '' : d)}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Question Type */}
        <div className="bulk-edit-field">
          <div className="bulk-edit-field-header">
            <label>Question Type</label>
            <span className="bulk-edit-mode-label">override</span>
          </div>
          <div className="bulk-edit-chips">
            {QUESTION_TYPES.map(qt => (
              <button
                key={qt}
                type="button"
                className={`bulk-edit-chip ${questionType === qt ? 'active' : ''}`}
                onClick={() => setQuestionType(questionType === qt ? '' : qt)}
              >
                {QUESTION_TYPE_LABELS[qt]}
              </button>
            ))}
          </div>
        </div>

        {/* Import Status (draft/live) */}
        <div className="bulk-edit-field">
          <div className="bulk-edit-field-header">
            <label>Status</label>
            <span className="bulk-edit-mode-label">override</span>
          </div>
          <div className="bulk-edit-chips">
            {STATUS_OPTIONS.map(s => (
              <button
                key={s}
                type="button"
                className={`bulk-edit-chip ${importStatus === s ? 'active' : ''}`}
                onClick={() => setImportStatus(importStatus === s ? '' : s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Question Readiness */}
        <div className="bulk-edit-field">
          <div className="bulk-edit-field-header">
            <label>Question Readiness</label>
            <span className="bulk-edit-mode-label">override</span>
          </div>
          <select
            value={questionReadiness ?? ''}
            onChange={e => setQuestionReadiness(e.target.value === '' ? null : Number(e.target.value))}
            className="bulk-edit-select"
          >
            <option value="">No change</option>
            {READINESS_LEVELS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div className="bulk-edit-field">
          <div className="bulk-edit-field-header">
            <label>Tags</label>
            <span className="bulk-edit-mode-label">add to all</span>
          </div>
          <div className="bulk-edit-chips">
            {availableTags.map(tag => (
              <button
                key={tag}
                type="button"
                className={`bulk-edit-chip tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                onClick={() => toggleArrayValue(selectedTags, tag, setSelectedTags)}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="bulk-edit-new-tag">
            <input
              type="text"
              placeholder="New tag..."
              value={newTagInput}
              onChange={e => setNewTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewTag(); } }}
              maxLength={30}
            />
            <button type="button" onClick={handleAddNewTag} disabled={!newTagInput.trim()}>
              Add
            </button>
          </div>
        </div>

        {/* Tag for teacher (admin-only) */}
        {teachers && teachers.length > 0 && onBulkTagForUser && (
          <div className="bulk-edit-field">
            <div className="bulk-edit-field-header">
              <label>Apply tag</label>
              <span className="bulk-edit-mode-label">teacher optional</span>
            </div>
            <div className="bulk-edit-teacher-tag">
              <select
                value={teacherTagUserId}
                onChange={e => setTeacherTagUserId(e.target.value)}
                className="bulk-edit-teacher-select"
              >
                <option value="">No teacher (you / system pool)</option>
                {teachers.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Tag name..."
                value={teacherTagName}
                onChange={e => setTeacherTagName(e.target.value)}
                maxLength={30}
                className="bulk-edit-teacher-tag-input"
              />
            </div>
          </div>
        )}

        {/* Result message */}
        {result && (
          <div className={`bulk-edit-result ${result.startsWith('Error') ? 'error' : 'success'}`}>
            {result}
          </div>
        )}

        {/* Actions */}
        <div className="bulk-edit-actions">
          <button type="button" className="bulk-edit-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bulk-edit-apply"
            onClick={handleApply}
            disabled={!hasChanges || isApplying}
          >
            {isApplying ? 'Applying...' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

